import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { CustomerFinancialSummary } from '../../types/customers';

export interface CustomerFinancialSummaryEntry {
  data: CustomerFinancialSummary | null;
  loading: boolean;
  error: string | null;
}

export interface CustomerFinancialSummaryState {
  byCustomerId: Record<string, CustomerFinancialSummaryEntry>;
}

const createInitialEntry = (): CustomerFinancialSummaryEntry => ({
  data: null,
  loading: false,
  error: null,
});

const initialState: CustomerFinancialSummaryState = {
  byCustomerId: {},
};

const ensureEntry = (
  state: CustomerFinancialSummaryState,
  customerId: string,
): CustomerFinancialSummaryEntry => {
  if (!state.byCustomerId[customerId]) {
    state.byCustomerId[customerId] = createInitialEntry();
  }
  return state.byCustomerId[customerId];
};

const customerFinancialSummarySlice = createSlice({
  name: 'backofficeCustomerFinancialSummary',
  initialState,
  reducers: {
    setCustomerFinancialSummaryLoading: (
      state,
      action: PayloadAction<{ customerId: string; loading: boolean }>,
    ) => {
      const entry = ensureEntry(state, action.payload.customerId);
      entry.loading = action.payload.loading;
      if (action.payload.loading) {
        entry.error = null;
      }
    },
    setCustomerFinancialSummaryError: (
      state,
      action: PayloadAction<{ customerId: string; error: string | null }>,
    ) => {
      const entry = ensureEntry(state, action.payload.customerId);
      entry.error = action.payload.error;
      entry.loading = false;
    },
    setCustomerFinancialSummary: (
      state,
      action: PayloadAction<{
        customerId: string;
        summary: CustomerFinancialSummary;
      }>,
    ) => {
      const entry = ensureEntry(state, action.payload.customerId);
      entry.data = action.payload.summary;
      entry.error = null;
      entry.loading = false;
    },
    clearCustomerFinancialSummary: (state, action: PayloadAction<string>) => {
      delete state.byCustomerId[action.payload];
    },
    /** Called on location switch — same pattern as `clearAllCustomerOrders`. */
    clearAllCustomerFinancialSummaries: () => initialState,
  },
});

export const {
  setCustomerFinancialSummaryLoading,
  setCustomerFinancialSummaryError,
  setCustomerFinancialSummary,
  clearCustomerFinancialSummary,
  clearAllCustomerFinancialSummaries,
} = customerFinancialSummarySlice.actions;

export default customerFinancialSummarySlice.reducer;
